import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * PD-14959 — when a user last opened a record's own page (a visit), for the
 * Recent list global search shows before anything is typed. Kept on the
 * server, per tenant user, so the list follows the user across devices and
 * the mobile app.
 *
 * `entityType` is one of the global search record types
 * (GlobalSearchRecordTypeModel: client, contact, item, order, quote, invoice,
 * issue, task) and `entityId` the record's `_id` in its own table (a task is
 * its `_event_occurrence` row). One row per (user, type, record): opening the
 * record again moves its date forward rather than adding a row. Only the
 * user's newest RECORD_VIEWS_KEPT rows are kept; older ones are hard-deleted
 * when a visit is recorded, unlike report_view, which soft-deletes and keeps
 * every row it ever wrote.
 *
 * Not audited, for the same reason as report_view: a row is written every
 * time a record page opens, and an `_audit` row for each would be noise that
 * says nothing about the data.
 */
@Index('idx_record_view_user_record', ['fkUserId', 'entityType', 'entityId'], {
	unique: true,
})
@Index('idx_record_view_user_viewed', ['fkUserId', 'lastViewedDate'], {})
@Entity('record_view', {}, { auditable: false })
export class RecordViewEntity extends BaseEntity {
	/** Tenant-local `user._id`, the id the session's instance user carries. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	@Column('varchar', { name: 'entityType', length: 32 })
	public entityType!: string;

	@Column('int', { name: 'entityId', unsigned: true })
	public entityId!: number;

	@Column('datetime', { name: 'lastViewedDate' })
	public lastViewedDate!: Date;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public user!: UserEntity;
}
